import { t } from 'i18next';
import { IOrderItem as IOrder } from '@/types';
import { IOrderItem } from '../types';
import { IConsumptionBag, IConsumptionBagTransformed, ChoicesStatusConsumptionBag } from '../types';
import moment from 'moment-timezone';

/**
 * Gets the unique identifier for an order item
 */
const getItemIdentifier = (item: any): string => {
  return item._id || item.product?._id || item.tempId || '';
};

/**
 * Retrieves and flattens all order items from different categories within an order
 * Combines items, itemsToAddLicenses, itemsToRenewal, and itemsToUpgrade arrays
 *
 * @param currentOrder - The order object containing various item arrays
 * @returns Sorted array of all order items from all categories
 */
export const getAllOrderItems = (currentOrder: IOrder | null): IOrderItem[] => {
  if (!currentOrder) return [];

  const itemsArrays = [
    currentOrder.items as IOrderItem[],
    currentOrder.itemsToAddLicenses as IOrderItem[],
    currentOrder.itemsToRenewal as IOrderItem[],
    currentOrder.itemsToUpgrade as IOrderItem[],
  ].filter(Boolean);

  const allItems = itemsArrays.flat();

  return allItems.sort((a, b) => {
    const aId = getItemIdentifier(a);
    const bId = getItemIdentifier(b);
    return aId.localeCompare(bId);
  });
};

/**
 * Gets the formatted label for a consumption bag
 * @param bag - The consumption bag object
 * @param formatter - Number formatter
 * @returns Formatted label string
 */
const getBagLabel = (bag: IConsumptionBag, formatter: Intl.NumberFormat): string => {
  const balance = formatter.format(bag.currentBalance || 0);
  const expirationDate = bag.expirationDate ? moment(bag.expirationDate).format('L') : '';

  return `${t('Current Balance: $ ')} ${balance} | ${t('Expires')}: ${expirationDate}`;
};

/**
 * Transforms raw consumption bag data into frontend-friendly format
 * @param bags - Array of raw consumption bags
 * @param formatter - Number formatter
 * @returns Array of transformed consumption bags
 */
export const transformConsumptionBags = (
  bags: IConsumptionBag[],
  formatter: Intl.NumberFormat
): IConsumptionBagTransformed[] => {
  return bags.map((bag) => ({
    ...bag,
    name: bag.sku?.name || bag.name || t('Unknown Bag'),
    label: bag.sku?.name || bag.name || t('Unknown Bag'),
    code: getBagLabel(bag, formatter),
    isExpired: bag.expirationDate ? new Date(bag.expirationDate) < new Date() : false,
    isActive: bag.status === ChoicesStatusConsumptionBag.ACTIVE,
    hasBalance: (bag.currentBalance || 0) > 0,
  }));
};
